import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { SharedModule } from "../shared/shared.module";
import { AnalyticsService } from "./analytics.service";
import { Observable, Subject } from "rxjs";
import {
  AnalStatsResponse,
  UsersStats,
  TopUser,
  TotalClicks,
  EmployeeSummary,
  ResourceClicks,
  ResourceTypeKey,
} from "./analytics.interface";
import { map, takeUntil, take } from "rxjs/operators";
import moment from "moment";
import { StoreExtendedService } from "../shared/store-extended.service";
import {
  GetAdvancedSettingsAction,
  ChangeAdvancedSettingsAction,
  GetBillingSettingsAction,
} from "../settings/settings.actions";
import {
  getAdvancedSettingsSelector,
  getBillingSettingsSelector,
} from "../settings/settings.reducer";
import { FormControl, FormGroup } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { MatChip } from "@angular/material/chips";
import { MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatIcon } from "@angular/material/icon";
import { MatSort } from "@angular/material/sort";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { AuthService } from "../auth/auth.service";
import { LimitPlanDialogComponent } from "../shared/limit-plan-dialog/limit-plan-dialog.component";
import {
  BillingSettings,
  AdvancedSettings,
} from "../settings/settings.interface";
import { isFeatureBlocked } from "../shared/utils/plan-restriction.util";

@Component({
  selector: "app-analytics-dashboard",
  imports: [SharedModule, MatChip, MatFormField, MatInput, MatIcon],
  templateUrl: "./analytics-dashboard.component.html",
  styleUrl: "./analytics-dashboard.component.scss",
})
export class AnalyticsDashboardComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  public analyticsResponse?: AnalStatsResponse;
  public totalClicks?: TotalClicks;
  public usersData!: UsersStats;
  public dataSource = new MatTableDataSource<EmployeeSummary>([]);
  public summaryLoading = true;
  public tableLoading = true;
  public selectedResourceType: ResourceTypeKey = "ALL";
  public billingSettings?: BillingSettings;

  public readonly displayedColumns = [
    "expand",
    "userEmail",
    "totalOpens",
    "totalClicks",
    "totalCTR",
    "totalAddons",
  ];
  public readonly dateRangeForm = new FormGroup({
    from: new FormControl(moment().startOf("month").toDate()),
    to: new FormControl(moment().toDate()),
  });

  public analyticLinksEnabled$: Observable<boolean>;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("analyticsToggle") analyticsToggle!: MatSlideToggle;

  constructor(
    private readonly service: AnalyticsService,
    private readonly store: StoreExtendedService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    private readonly auth: AuthService,
  ) {}

  ngOnInit() {
    this.initializeComponent();
  }

  ngAfterViewInit() {
    this.setupSorting();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilter(event: Event) {
    this.dataSource.filter = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();
  }

  onResourceClick(resourceType: ResourceTypeKey) {
    this.selectedResourceType = resourceType;
    this.updateTableData();
  }

  toggleRow(element: EmployeeSummary) {
    if (!element.templates?.length) {
      return;
    }
    element.isExpanded = !element.isExpanded;
  }

  formatCTR(ctr: number): string {
    return `${(ctr * 100).toFixed(2)}%`;
  }

  formatResourceTooltip(resource: ResourceClicks): string {
    if (!resource.resourceName) {
      return "";
    }
    const clickText =
      resource.clicks === 1
        ? (this.translate.instant("analytics.tooltip.click") as string)
        : resource.clicks < 5
          ? (this.translate.instant("analytics.tooltip.clicks") as string)
          : (this.translate.instant("analytics.tooltip.clicksMany") as string);
    return `${resource.resourceName} - ${resource.clicks} ${clickText}`;
  }

  assertColumnItemType(item: TopUser): TopUser {
    return item;
  }

  trackByResourceId(
    index: number,
    item: { resourceId: string; resourceType: string },
  ): string {
    return item.resourceId + "_" + item.resourceType;
  }

  toggleAnalytics(enabled: boolean): void {
    if (isFeatureBlocked(this.auth.isSuperAdmin, this.billingSettings)) {
      if (enabled) {
        this.analyticsToggle.checked = false;
        const planName =
          this.billingSettings?.planName?.toLowerCase() || "basic";
        this.showFreemiumRestrictionDialog(
          planName as "free" | "freemium" | "basic",
        );
        return;
      }
    }

    this.store.dispatch(new GetAdvancedSettingsAction());
    this.store
      .select(getAdvancedSettingsSelector)
      .pipe(take(1))
      .subscribe((settings) => {
        if (settings) {
          const updatedSettings = {
            ...settings,
            analyticLinksEnabled: enabled,
          };
          this.store
            .dispatchAndWaitForResult(
              new ChangeAdvancedSettingsAction(updatedSettings),
            )
            .subscribe(() => {
              this.store.dispatch(new GetAdvancedSettingsAction());
            });
        }
      });
  }

  showFreemiumRestrictionDialog = (plan: "free" | "freemium" | "basic") => {
    this.dialog
      .open(LimitPlanDialogComponent, {
        width: "500px",
        maxWidth: "90vw",
        data: { plan, type: "analytics" },
      })
      .afterClosed()
      .subscribe(() => {});
  };

  private initializeComponent() {
    this.setupTable();
    this.loadData();
    this.setupDateRangeSubscription();
    this.loadBillingSettings();
  }

  private loadBillingSettings() {
    this.store.dispatch(new GetBillingSettingsAction());
    this.store
      .select(getBillingSettingsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((billingSettings: BillingSettings) => {
        if (billingSettings) {
          this.billingSettings = billingSettings;
        }
      });
  }

  private setupDateRangeSubscription() {
    this.dateRangeForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ from, to }) => {
        if (from && to) {
          this.loadData();
        }
      });
  }

  private setupSorting() {
    setTimeout(() => this.sort && (this.dataSource.sort = this.sort));
  }

  private setupTable() {
    this.dataSource.filterPredicate = this.createFilterPredicate();
    this.dataSource.sortingDataAccessor = this.createSortingAccessor();
  }

  private createFilterPredicate() {
    return (data: EmployeeSummary, filter: string) => {
      const searchStr = filter.toLowerCase();
      const searchableFields = [
        data.userEmail,
        data.totalOpens.toString(),
        data.totalClicks.toString(),
        this.formatCTR(data.totalCTR),
        data.totalAddons.map((a) => a.resourceName || "").join(", "),
        ...data.templates.map((t) => t.templateId ?? ""),
      ];
      return searchableFields.some((field) =>
        field?.toLowerCase().includes(searchStr),
      );
    };
  }

  private createSortingAccessor() {
    return (item: EmployeeSummary, property: string) => {
      const sortMap: Record<string, () => string | number> = {
        userEmail: () => item.userEmail.toLowerCase(),
        totalOpens: () => item.totalOpens,
        totalClicks: () => item.totalClicks,
        totalCTR: () => item.totalCTR,
        totalAddons: () =>
          item.totalAddons
            .map((a) => a.resourceName || "")
            .join(", ")
            .toLowerCase(),
      };
      return sortMap[property]?.() || "";
    };
  }

  private updateTableData() {
    const dataMap = {
      ALL: this.usersData?.totalClicks || [],
      BANNER: this.usersData?.addonBannerClicks || [],
      LINK: this.usersData?.addonLinkClicks || [],
      TEMPLATE: this.usersData?.templateClicks || [],
    };

    this.dataSource.data = dataMap[this.selectedResourceType].map((user) =>
      this.mapUserToEmployeeSummary(user),
    );
    this.setupSorting();
  }

  private mapUserToEmployeeSummary(user: TopUser): EmployeeSummary {
    return {
      userEmail: user.userEmail,
      totalOpens: user.opens || 0,
      totalClicks: user.clicks,
      totalCTR: user.ctr,
      totalAddons: user.topResourceClicks || [],
      templates: user.templates || [],
      isExpanded: false,
    };
  }

  private loadData() {
    this.summaryLoading = true;
    this.tableLoading = true;

    this.analyticLinksEnabled$ = this.store
      .dispatchAndWaitForResult(new GetAdvancedSettingsAction())
      .pipe(
        takeUntil(this.destroy$),
        map(
          ({ success, payload }) =>
            success && (payload as AdvancedSettings).analyticLinksEnabled,
        ),
      );

    const { from, to } = this.dateRangeForm.getRawValue();
    const dateRange = {
      from: moment(from).startOf("day").toDate(),
      to: moment(to).endOf("day").toDate(),
    };

    this.service.getAnalytics(dateRange).subscribe((data) => {
      this.analyticsResponse = data;
      this.usersData = data.users;
      this.totalClicks = data.clicks;
      this.dataSource.data = data.users.totalClicks.map((user) =>
        this.mapUserToEmployeeSummary(user),
      );
      this.setupSorting();
      this.summaryLoading = false;
      this.tableLoading = false;
    });
  }
}
